import { useMutation } from '@tanstack/react-query';
import { Col, Form, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { ISearchRouteComboBox } from 'apis/GraphQL/Queries/saleLineQuery';
import { PlantApi } from 'apis/PlantApis';
import { SaleLineApi } from 'apis/SaleLineApi';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { FormFilter, SelectForm } from 'components';
import DatePickerForm from 'components/DatePicker/DatePickerForm';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import { IFilterTeamSaleCb } from 'interfaces/TeamSale/TeamSale';
import Content from 'layouts/Content';
import { useEffect, useMemo, useState } from 'react';
import convertToLabelValueCbb from 'utils/convertToLabelValueCbb';

const spanCol = 6;

export interface FilterTeamSaleRevenueProps {
  defaultFilter?: any;
  onFilter?: (values: any) => void;
  onCancel?: () => void;
}

const FilterTeamSaleRevenue = ({
  defaultFilter,
  onCancel,
  onFilter,
}: FilterTeamSaleRevenueProps) => {
  const { user } = useUser();
  const [form] = useForm();
  const [plants, setPlants] = useState<DefaultOptionType[]>([]);
  const [routes, setRoutes] = useState<{
    items: any;
    pageInfo: any;
  }>({
    items: [],
    pageInfo: {},
  });
  const [teamSaleData, setTeamSaleData] =
    useState<ILazySelectData>(initLazySelectData);
  const teamOptions = useMemo(
    () =>
      ((user as any)?.teamSales ?? []).map((item: any) => ({
        label: item.subId + ' - ' + item.name,
        value: item.subId,
      })) ?? [],
    [user],
  );

  const { mutate: getAllPlant, isPending: getAllPlantLoading } = useMutation({
    mutationFn: () => PlantApi.getAllPlantComboBox(),
    onSuccess: (data: any) => {
      const items = data.data?.getPlant?.items || [];

      if (!items?.length) return setPlants([]);

      setPlants(
        items.map((item: any) => ({
          label: item.id + (item.name ? ` - ${item.name}` : ''),
          value: item.id,
        })),
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { isPending: getRouteLoading, mutate: getRouteCb } = useMutation({
    mutationFn: (params: ISearchRouteComboBox) =>
      SaleLineApi.getAllRouteComboBox(params),
    onSuccess: (res) => {
      const items = res.data?.getRouteM?.items || [];

      setRoutes({
        items: convertToLabelValueCbb(items),
        pageInfo: res.data?.getRouteM.pageInfo,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { isPending: getTeamSaleLoading, mutate: getTeamSales } = useMutation({
    mutationFn: (params: IFilterTeamSaleCb) =>
      SaleTeamApi.getAllTeamSaleCbSubSort(params),
    onSuccess: (res) => {
      if (res?.data?.getTeamSales) {
        const items = res.data?.getTeamSales?.items ?? [],
          totalItems = res.data?.getTeamSales?.pageInfo?.totalItems ?? 0;

        setTeamSaleData({
          options: items.map((item: any) => ({
            label: item.subId + ' - ' + item.name,
            value: `${item.id}.${item.subId}.${item.plantId}`,
          })),
          totalItems: totalItems,
        });
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleRouteSelectScrollChange = (params: ILazySelectParams) => {
    const plant = form.getFieldValue('plantId');
    getRouteCb({
      value: params.value,
      plantId: plant || defaultFilter.plantId,
      pageSize: params.pageSize,
    });
  };

  const handleTeamSaleScrollChange = (params: ILazySelectParams) => {
    const plant = form.getFieldValue('plantId');
    const teamSaleParams: IFilterTeamSaleCb = {
      ...params,
      plantId: plant || defaultFilter.plantId,
    };

    getTeamSales(teamSaleParams);
  };

  const handleChangePlant = (value: any) => {
    form.setFieldsValue({
      teamSalesId: undefined,
      routeId: undefined,
      plantId: value,
    });

    handleTeamSaleScrollChange({
      pageSize: 20,
      value: '',
    });

    handleRouteSelectScrollChange({
      pageSize: 20,
      value: '',
    });
  };

  const handleFinish = (values: any) => {
    onFilter?.(values);
  };

  const handleCancel = () => {
    form.resetFields();

    form.setFieldsValue({
      plantId: defaultFilter?.plantId,
      month: defaultFilter?.month,
    });

    onCancel?.();
  };

  useEffect(() => {
    getAllPlant();
  }, [getAllPlant]);

  useEffect(() => {
    if (defaultFilter) {
      form.setFieldsValue({
        plantId: defaultFilter.plantId,
        month: defaultFilter.month,
      });
    }
  }, [defaultFilter.plantId, form]);

  return (
    <Content isOpen>
      <FormFilter
        okTitle="Xem báo cáo"
        form={form}
        onFinish={handleFinish}
        onCancel={handleCancel}
      >
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item
              label="Chi nhánh"
              name="plantId"
              // rules={[{ required: true, message: 'Vui lòng chọn chi nhánh' }]}
            >
              <SelectForm
                loading={getAllPlantLoading}
                options={plants}
                placeholder="Chọn chi nhánh"
                disabled={user?.plantCode !== '5000'}
                onChange={handleChangePlant}
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Tuyến bán hàng" name="routeId">
              {/* <SelectForm
                loading={getRouteLoading}
                placeholder="Chọn tuyến"
                options={routes.items}
              /> */}
              <LazySelectForm
                customeSize={20}
                data={{
                  options: routes.items || [],
                  totalItems: routes.pageInfo?.totalItems || 0,
                }}
                loading={getRouteLoading}
                placeholder="Chọn tuyến"
                onSelectScrollChange={handleRouteSelectScrollChange}
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item
              label="Đội bán hàng"
              name="teamSalesId"
              initialValue={defaultFilter?.teamSalesSubId}
            >
              {user?.isSale ? (
                <SelectForm
                  disabled
                  placeholder="Chọn đội"
                  options={teamOptions}
                />
              ) : (
                <LazySelectForm
                  customeSize={20}
                  data={teamSaleData}
                  placeholder="Chọn đội"
                  loading={getTeamSaleLoading}
                  onSelectScrollChange={handleTeamSaleScrollChange}
                />
              )}
            </Form.Item>
          </Col>

          {/* <Col span={spanCol} className="h-[80px]">
            <Form.Item
              label="Thời gian xuất báo cáo"
              name="year"
              // rules={[{ required: true, message: 'Vui lòng chọn năm' }]}
            >
              <RangePickerForm
                maxDate={dayjs()}
                placeholder={['Từ ngày', 'Đến ngày']}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col> */}

          <Col span={spanCol} className="h-[80px]">
            <Form.Item
              label="Thời gian xuất báo cáo"
              name="month"
              rules={[
                {
                  required: true,
                  message: 'Chọn thời gian xuất báo cáo',
                },
              ]}
            >
              <DatePickerForm
                maxDate={dayjs()}
                placeholder="Chọn tháng"
                format="MM/YYYY"
                picker="month"
                allowClear={false}
              />
            </Form.Item>
          </Col>
        </Row>
      </FormFilter>
    </Content>
  );
};

export default FilterTeamSaleRevenue;
